import { useEffect, useRef, useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";

import {
  useGetContentById,
  useUpdateContentMutation,
} from "../../../../../api/jeep-rally/content/content.hooks";
import { isJeepRallyAdmin } from "../../Categories/utils/category.utils";
import ContentForm from "../_components/ContentForm";
import {
  buildContentUpdatePayload,
  deepClone,
  formatPageLabel,
  getContentApiErrorMessage,
  normalizeContent,
  THEME_FOCUS_ID,
  validateContentObject,
} from "../utils/content.utils";

const ContentUpdatePage = () => {
  const history = useHistory();
  const { id, pageId } = useParams();
  const focusId = pageId || history.location.state?.focusId || THEME_FOCUS_ID;
  const canManage = isJeepRallyAdmin();
  const locationContent = history.location.state?.content || null;
  const hydratedIdRef = useRef(null);
  const forceHydrateRef = useRef(false);
  const uploadCountRef = useRef(0);

  const {
    data: contentData,
    isLoading: isContentLoading,
    isError: isContentError,
    error: contentError,
    refetch,
  } = useGetContentById(id);

  const document =
    normalizeContent(contentData) ||
    (locationContent &&
    (locationContent._id || locationContent.id) === id
      ? locationContent
      : null);

  const [content, setContent] = useState(() =>
    deepClone(document?.content || {})
  );
  const [validationErrors, setValidationErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [uploading, setUploading] = useState(false);

  const {
    mutateAsync: updateContent,
    isLoading,
    isPending,
    reset,
  } = useUpdateContentMutation();

  const submitting = isLoading || isPending;
  const isThemeFocus = focusId === THEME_FOCUS_ID;
  const pageTitle = isThemeFocus
    ? "Update Theme"
    : `Update ${formatPageLabel(focusId)}`;

  useEffect(() => {
    hydratedIdRef.current = null;
    forceHydrateRef.current = false;
  }, [id]);

  useEffect(() => {
    const next = normalizeContent(contentData);
    if (!next?.content) return;

    const shouldHydrate =
      hydratedIdRef.current !== id || forceHydrateRef.current;
    if (!shouldHydrate) return;

    setContent(deepClone(next.content));
    hydratedIdRef.current = id;
    forceHydrateRef.current = false;
    setValidationErrors({});
  }, [contentData, id]);

  const goBack = () => history.push("/jeep-rally/content");

  const handleContentChange = (nextContent) => {
    setContent(nextContent);
    setValidationErrors((prev) => ({ ...prev, content: undefined }));
    setSuccessMessage("");
  };

  const handleUploadStart = () => {
    uploadCountRef.current += 1;
    setUploading(true);
  };

  const handleUploadEnd = () => {
    uploadCountRef.current = Math.max(0, uploadCountRef.current - 1);
    if (uploadCountRef.current === 0) setUploading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canManage || !id) return;

    reset();
    setSubmitError("");
    setSuccessMessage("");

    // Always send the full content object (theme + all pages) to the API.
    const nextErrors = validateContentObject(content);
    setValidationErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    try {
      await updateContent({
        contentId: id,
        payload: buildContentUpdatePayload(content),
      });
      forceHydrateRef.current = true;
      setSuccessMessage(
        isThemeFocus
          ? "Theme updated successfully."
          : `${formatPageLabel(focusId)} updated successfully.`
      );
      await refetch();
    } catch (error) {
      setSubmitError(
        getContentApiErrorMessage(error, "Failed to update content.")
      );
    }
  };

  if (isContentLoading && !document) {
    return (
      <div className="container-fluid">
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" />
        </div>
      </div>
    );
  }

  if ((isContentError && !document) || !document) {
    return (
      <div className="container-fluid">
        <Alert variant="danger" className="mb-3">
          {getContentApiErrorMessage(
            contentError,
            "Content not found. Please open it from the Content list."
          )}
        </Alert>
        <button type="button" className="btn btn-primary" onClick={goBack}>
          Back to content list
        </button>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="d-flex align-items-center gap-2 mb-4">
        <button
          type="button"
          className="btn btn-link p-0 text-decoration-none"
          onClick={goBack}
          aria-label="Back to content list"
          title="Back"
        >
          <i className="bi bi-arrow-left fs-4" />
        </button>
        <h4 className="mb-0">{pageTitle}</h4>
      </div>

      {!canManage && (
        <Alert variant="warning" className="mb-4">
          Admin access is required to update site content.
        </Alert>
      )}

      {successMessage && (
        <Alert variant="success" className="mb-4">
          {successMessage}
        </Alert>
      )}

      {submitError && (
        <Alert variant="danger" className="mb-4">
          {submitError}
        </Alert>
      )}

      <ContentForm
        content={content}
        focusId={focusId}
        validationErrors={validationErrors}
        submitting={submitting}
        uploading={uploading}
        readOnly={!canManage}
        onSubmit={handleSubmit}
        onContentChange={handleContentChange}
        onUploadStart={handleUploadStart}
        onUploadEnd={handleUploadEnd}
        onBack={goBack}
      />
    </div>
  );
};

export default ContentUpdatePage;
